import { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../../shared/api/client';
import type { ReactNode } from 'react';
import { StepHeading } from '../../../shared/ui/StepHeading';
import { useWakeLock } from '../../../shared/lib/useWakeLock';
import * as Sentry from '@sentry/react';
import { reportError } from '../../../shared/lib/reportError';
import { ReelPreview } from '../../reel-preview';
import { ExportReelButton } from '../../../features/reel-export';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { describeStoryFailures } from '../../../shared/lib/storyFailures';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Loader2, Upload, Send, Video, X } from 'lucide-react';
import { useAccountStore, type Account } from '../../../entities/account';
import { useTemplateStore } from '../../../entities/template';
import { useReelPublishStore } from '../model/useReelPublishStore';
import { toast } from 'sonner';
import { saveEnrichThemes } from '../../../features/statistics/api/account-report';
import { isBundleStale, STALE_BUNDLE_MESSAGE } from '../../../shared/lib/bundleFreshness';
import { reelsApi, submitReelJobServer, claimReelJob, isServerUnavailable, JobLostError, UploadFailedError, pollReelJobStatus, cancelReelJob, type ReelJobEvent } from '../../../shared/api/reels.api';
import { uploadApi, needsRemux, processedBlobNeedsRemux } from '../../../shared/api/upload.api';
import { measureAsync } from '../../../shared/lib/measureAsync';
import { haptic } from '../../../shared/lib/usePinchFontSize';
import { VideoProcessor } from '../../../features/reel-export/ui/VideoProcessor';
import { ReelNormalizer } from '../../../features/reel-export/ui/ReelNormalizer';
import { AccountsHorizontalList } from '../../accounts-horizontal-list';
import { GenerateCaptionsBulkButton, GenerateTitlesBulkButton } from '../../../features/caption-generate';
import {
  PublishConfirmationModal, WhenToPublish, accountsToPublish, missingForReel, missingText, montageReturn, parseScheduled, publishActionLabel, useIsDesktop,
  type AccountPublishStatus, type MissingField,
} from '../../../features/post-publish';
import { ROUTES } from '../../../shared/config/routes';
import type { PublicationCategory } from '../../../shared/types/common';
import { DEFAULT_PREVIEW_FONT_SIZE } from '../../../shared/lib/video';
import { resolvePublishToast, type PublishResult } from '../lib/publishToast';
import { publishReelsParallel } from '../lib/publishReels';
import { buildRenderTasks, buildPlainRenderTask } from '../lib/buildRenderTasks';
import { uploadProgressToStatus, serverEventToStatus, retryNoticeMessage } from '../lib/serverProgress';
import { applyResumeEvent } from '../lib/resumeProgress';
import { saveActiveJob, loadActiveJob, clearActiveJob, type ActiveJob } from '../lib/jobSession';
import { sendPublishTelemetry } from '../../../shared/lib/telemetry';
import { mapIgError } from '../../../shared/lib/igError';
import { savePublishResult, loadPublishResult, clearPublishResult } from '../../../shared/lib/publishResultStore';
import { bigFileWarning, isStalled } from '../../../shared/lib/uploadWarning';
import { SELECTABLE_CATEGORIES, defaultCategory } from '../../../shared/lib/publicationCategories';
import { copyrightApi, type CopyrightStatus } from '../../../shared/api/copyright.api';
import { CopyrightBanner } from '../../../features/copyright-check/ui/CopyrightBanner';
import { useWebCodecsProcessor } from '../../../shared/lib/webcodecs';
import type { OverlayParams } from '../../../shared/lib/webcodecs';
import { buildOverlayParams } from '../../../shared/lib/webcodecs/buildOverlayParams';
import { CollaboratorsInput } from '../../../features/collaborators-input';
import { MusicSection, type MusicSelection } from './MusicSection';
import { type PublishMode } from '../../../shared/lib/publishMode';
import { fromDateTimeLocal } from '../../../shared/lib/datetime-local';
import { canPublishVia } from '../../../entities/account/lib/status';
import { FontSizeStepper } from '../../../shared/ui/FontSizeStepper';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser';
import {
  AccountGroupsBar, LastSetNotice, TextScope, WorkBriefToggle, reportPublished, saveLastAccounts, scopeTargets, useAccountGroups, useDraftAccounts,
  useLogsWork, useMontageItemAccount, usePublishDraft, workTitleFrom,
} from '../../../features/publish-groups';

type TemplateMode = 'with' | 'without';


/** Переключатель формата рисует страница — форма даёт ему место в своей колонке. */
interface CreateFormProps {
  headerSlot?: ReactNode;
}

export function ReelCreateForm({ headerSlot }: CreateFormProps = {}) {
  const [videoFile, setVideoFile] = useState<File | null>(null);
  // Ролик из «Монтажа» (кнопка «Опубликовать» на одобренном ролике): форма
  // сама скачивает оригинал, ставит первыми аккаунты направления и после
  // выкладки прикладывает к ролику ссылку на пост.
  const [searchParams] = useSearchParams();
  const montageTask = searchParams.get('montageTask');
  const montageItem = searchParams.get('montageItem');
  // Ролик B2G у раздающей Редакции (08.10.2026) — тот же путь, другие адреса:
  // файл из «Роликов B2G», после публикации — отметка «Взяла» со ссылкой.
  const takeItem = searchParams.get('takeItem');
  const montageFrom = takeItem
    ? `/montage/takes/items/${encodeURIComponent(takeItem)}`
    : montageTask && montageItem
      ? `/montage/tasks/${encodeURIComponent(montageTask)}/items/${encodeURIComponent(montageItem)}`
      : null;
  const [montageAccountIds, setMontageAccountIds] = useState<string[]>([]);
  const [montageLoading, setMontageLoading] = useState(false);
  const [montageTitle, setMontageTitle] = useState<string | null>(null);
  // Ответ /source пришёл: до него не знаем аккаунтов направления.
  const [montageSourceReady, setMontageSourceReady] = useState(false);
  const montageItemAccount = useMontageItemAccount(takeItem ? null : montageTask, takeItem ? null : montageItem, searchParams.get('montageAccount'));
  const returnTo = montageReturn({ montageTask, montageItem, takeItem });
  const [videoDurationSec, setVideoDurationSec] = useState<number | null>(null);
  // Current in-flight publish jobId — set when submitReelJobServer starts,
  // cleared when the publish settles. Used to drive the user-cancel endpoint.
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [introImageFile, setIntroImageFile] = useState<File | null>(null);
  const [introImagePreviewUrl, setIntroImagePreviewUrl] = useState<string | null>(null);
  const [textPosition, setTextPosition] = useState<{ x: number; y: number; previewWidth: number; previewHeight: number; textBoxWidth: number; bottom?: number; lines?: string[]; fontSize?: number } | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  // ponytail: публикация только через API; телефонная логика сохранена, UI-переключатель убран
  const publishMode: PublishMode = 'api';
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Описания — в общем черновике экрана: переживают переключение рилс ↔ пост.
  const captions = usePublishDraft((s) => s.captions);
  const setCaptions = usePublishDraft((s) => s.setCaptions);
  const setDraftHasMedia = usePublishDraft((s) => s.setHasMedia);
  const [titles, setTitles] = useState<Map<string, string>>(new Map());
  const [templateMode, setTemplateMode] = useState<TemplateMode>('with');
  const [textDuration, setTextDuration] = useState<number | 'full'>('full'); // 3, 5, 10 секунд или 'full' для всего видео
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishDone, setPublishDone] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);
  // In-window notice while the backend auto-retries an IG-side transient failure.
  const [retryNotice, setRetryNotice] = useState<string | null>(null);
  const lastProgressRef = useRef<number>(0);
  useWakeLock(publishing);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedBlob, setProcessedBlob] = useState<Blob | null>(null);
  const [processingQueue, setProcessingQueue] = useState<Array<{
    accountId: string;
    logoFile: File | null;
    resolve: (blob: Blob) => void;
    reject: (err: any) => void;
  }>>([]);
  const { process: processWithWebCodecs, isSupported: webCodecsSupported } = useWebCodecsProcessor();
  const [isNormalizing, setIsNormalizing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishStatuses, setPublishStatuses] = useState<AccountPublishStatus[]>([]);
  const [publicationCategory, setPublicationCategory] = useState<PublicationCategory | undefined>(() => defaultCategory(!!takeItem));
  // Раздающая (Саяна) помечает ролик для «Отработано» прямо здесь (26.10.2026).
  const logsWork = useLogsWork();
  const [withBrief, setWithBrief] = useState(true);
  // «Текст для: Русские» — заголовок и описание правятся у группы разом.
  const accountGroups = useAccountGroups();
  const [textScope, setTextScope] = useState<string | null>(null);
  const { userId } = useCurrentUser();
  // Отложенная публикация: строка datetime-local (локальное время), пусто = сразу.
  const [scheduledAt, setScheduledAt] = useState('');
  // Текст в окне публикации, когда работа ушла в очередь вместо публикации.
  const [scheduledNotice, setScheduledNotice] = useState<string | null>(null);
  // Тот же факт, но читаемый синхронно: состояние ещё не долетит до кода,
  // который сразу после пайплайна решает, показывать ли общий тост.
  const scheduledRef = useRef(false);
  // batchId последнего пакета — по нему окно «Готово» найдёт ссылки на посты.
  const [lastBatchId, setLastBatchId] = useState<string | null>(null);
  // Ширина ≥ lg — превью справа; уже — превью в потоке формы, под аккаунтами.
  const isDesktop = useIsDesktop();
  const [previewOpen, setPreviewOpen] = useState(true);
  const [fontSizes, setFontSizes] = useState<Map<string, number>>(new Map());
  // Режим «менять размер пальцами» на превью рилса.
  const [sizeEditActive, setSizeEditActive] = useState(false);
  const [copyrightStatus, setCopyrightStatus] = useState<CopyrightStatus>('idle');
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const [music, setMusic] = useState<MusicSelection | null>(null);
  const [duplicateToStory, setDuplicateToStory] = useState(false);
  const [overallStatus, setOverallStatus] = useState<{
    phase: 'processing' | 'uploading' | 'remuxing' | 'publishing';
    current: number;
    total: number;
    progress?: number;
    percent: number;
    indeterminate?: boolean;
  } | null>(null);
  const copyrightCheckIdRef = useRef<number>(0);
  const publishAbortRef = useRef<AbortController | null>(null);

  const accounts = useAccountStore((state) => state.accounts);
  const accountsLoading = useAccountStore((state) => state.isLoading);
  const accountsError = useAccountStore((state) => state.error);
  // «Телефон» → только phone-аккаунты (залогинены в приложении), «API» → только graph/OAuth.
  const activeAccounts = accounts.filter(acc => acc.isActive && canPublishVia(acc, publishMode));
  // Отмеченные аккаунты — общий черновик рилса и поста; набор по умолчанию
  // ставит хук: аккаунт ролика из «Монтажа» или «как в прошлый раз».
  const {
    checked: checkedAccountIds, setChecked: setCheckedAccountIds, lastIds: lastAccountIds, appliedLast, resetToEmpty, refreshLast,
  } = useDraftAccounts({
    userId,
    activeIds: activeAccounts.map((a) => a.id),
    montage: {
      active: !!montageFrom && !takeItem,
      resolved: montageSourceReady && montageItemAccount.resolved,
      itemAccountId: montageItemAccount.accountId,
      projectAccountIds: montageAccountIds,
    },
  });
  const templates = useTemplateStore((state) => state.templates);
  const setPublishState = useReelPublishStore((state) => state.setPublishState);
  const resetPublishState = useReelPublishStore((state) => state.reset);

  const videoInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const introImageInputRef = useRef<HTMLInputElement>(null);

  // Получаем все уникальные тэги из активных аккаунтов
  const allTags = Array.from(
    new Set(
      activeAccounts.flatMap(acc => acc.tags || [])
    )
  ).sort();

  // Фильтруем аккаунты по выбранным тэгам и поисковому запросу
  let filteredAccounts = selectedTags.size > 0
    ? activeAccounts.filter(acc => 
        acc.tags?.some(tag => selectedTags.has(tag))
      )
    : activeAccounts;

  // Применяем поисковый фильтр
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filteredAccounts = filteredAccounts.filter(acc => 
      acc.name.toLowerCase().includes(query) || 
      acc.username?.toLowerCase().includes(query)
    );
  }

  // Сортируем аккаунты: с шаблонами первыми
  const sortedAccounts = [...filteredAccounts].sort((a, b) => {
    // Аккаунт самого ролика, за ним аккаунты направления из «Монтажа» — первыми.
    const aOwn = a.id === montageItemAccount.accountId;
    const bOwn = b.id === montageItemAccount.accountId;
    if (aOwn !== bOwn) return aOwn ? -1 : 1;
    const aDirection = montageAccountIds.includes(a.id);
    const bDirection = montageAccountIds.includes(b.id);
    if (aDirection !== bDirection) return aDirection ? -1 : 1;
    const aHasTemplate = templates.some(t => t.accountId === a.id && (t.type ?? 'reel') === 'reel');
    const bHasTemplate = templates.some(t => t.accountId === b.id && (t.type ?? 'reel') === 'reel');
    if (aHasTemplate && !bHasTemplate) return -1;
    if (!aHasTemplate && bHasTemplate) return 1;
    return 0;
  });

  // Обработчик клика по тэгу
  const handleTagClick = (tag: string) => {
    setSelectedTags(prev => {
      const newSet = new Set(prev);
      const isAdding = !newSet.has(tag);
      
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      
      // Автоматически проставляем/снимаем чекбоксы
      setCheckedAccountIds(prevChecked => {
        const newChecked = new Set(prevChecked);
        
        // Находим все аккаунты с этим тегом
        const accountsWithTag = activeAccounts.filter(acc => acc.tags?.includes(tag));
        
        if (isAdding) {
          // Добавляем все аккаунты с этим тегом
          accountsWithTag.forEach(acc => newChecked.add(acc.id));
        } else {
          // Удаляем только те аккаунты, у которых нет других выбранных тегов
          accountsWithTag.forEach(acc => {
            const hasOtherSelectedTags = acc.tags?.some(t => t !== tag && newSet.has(t));
            if (!hasOtherSelectedTags) {
              newChecked.delete(acc.id);
            }
          });
        }
        
        return newChecked;
      });
      
      return newSet;
    });
  };

  // Получаем шаблон выбранного аккаунта
  const selectedTemplate = selectedAccountId 
    ? templates.find(t => t.accountId === selectedAccountId && (t.type ?? 'reel') === 'reel')
    : null;

  // Получаем выбранный аккаунт для генерации
  const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);

  // Ручной размер выбранного аккаунта. null = авто: кегль подберёт wrapTextLines,
  // в пикселях кадра. Раньше тут стояли зашитые 24, домноженные на ширину
  // превью — дежурный правил размер на каждом рилсе, а результат ещё и зависел
  // от ширины окна браузера.
  const currentFontSize = selectedAccountId
    ? (fontSizes.get(selectedAccountId) ?? null)
    : null;
  // Что подобрал рендер (preview-space px) — превью сообщает через textPosition.
  const resolvedFontSize = currentFontSize ?? textPosition?.fontSize ?? DEFAULT_PREVIEW_FONT_SIZE;

  // Resume an active job if one is present in localStorage (e.g. Telegram WebView
  // killed the tab between publish-start and now). We open the publish modal in
  // "polling-only" mode: the bar shows the polled state, and the same publish
  // toasts fire on completion.
  useEffect(() => {
    const active: ActiveJob | null = loadActiveJob();
    if (!active) {
      // No resumable job — check for a previous publish result snapshot to restore.
      const snap = loadPublishResult();
      if (snap && snap.kind === 'reel') {
        setPublishStatuses(snap.accounts.map(a => ({
          account: { id: a.id ?? a.name, name: a.name, profilePicture: a.profilePicture } as Account,
          status: a.status,
          caption: '',
          error: a.status === 'error' ? a.friendly : undefined,
        })));
        if (snap.state === 'error') setPublishError(snap.errorReason ?? 'Загрузка не удалась');
        else setPublishDone(true);
        setShowPublishModal(true);
      }
      return;
    }
    if (publishing) return; // a fresh publish is already in flight in this tab

    setPublishing(true);
    setShowPublishModal(true);
    // We don't know per-account names from localStorage alone; show ids.
    setPublishStatuses(
      active.accountIds.map((id) => ({
        account: { id, name: id } as Account,
        status: 'publishing' as const,
        caption: '',
      })),
    );
    setOverallStatus({ phase: 'publishing', current: 1, total: active.accountIds.length, percent: 0 });
    // Surface the jobId so the modal's close→cancel can target the backend job.
    // Without this, onUserCancel short-circuits on a resumed job (currentJobId
    // would stay null) and the cancel POST never fires.
    setCurrentJobId(active.jobId);
    // Give the resumed poll an abort handle so the modal's close→cancel (which
    // calls publishAbortRef.current?.abort()) tears the poller down instead of
    // letting it resolve a stale 'done' after the user cancelled.
    const resumeAbort = new AbortController();
    publishAbortRef.current = resumeAbort;

    (async () => {
      try {
        // Running terminal count so the bar reflects the real backend phase/
        // percent on resume instead of restarting from 0%.
        let resumePublished = 0;
        const events = await pollReelJobStatus(active.jobId, {
          signal: resumeAbort.signal,
          onEvent: (event) => {
            // Retry-in-progress notice, consistent with the live pipeline.
            if (event.phase === 'publish' && event.status === 'retrying') {
              setRetryNotice(retryNoticeMessage(event.attempt));
            }
            const { overall, publishedCount, account } = applyResumeEvent(event, active.accountIds, resumePublished);
            resumePublished = publishedCount;
            if (account) {
              setRetryNotice(null);
              setPublishStatuses((prev) =>
                prev.map((s, i) =>
                  i === account.index
                    ? { ...s, status: account.status, error: account.error ? mapIgError(account.error).friendly : undefined }
                    : s,
                ),
              );
            }
            if (overall) {
              setOverallStatus((prev) => (prev ? { ...overall, percent: Math.max(prev.percent, overall.percent) } : overall));
            }
          },
        });
        const done = events.find((e) => e.phase === 'done');
        if (done && done.phase === 'done') {
          const results = (done.results ?? []).map((r) => ({
            index: active.accountIds.indexOf(r.accountId),
            status: r.status,
            error: r.error,
          }));
          const toastSpec = resolvePublishToast(results);
          const description = 'description' in toastSpec ? toastSpec.description : undefined;
          if (toastSpec.variant === 'error') toast.error(toastSpec.message, description ? { description } : undefined);
          else if (toastSpec.variant === 'warning') toast.warning(toastSpec.message, description ? { description } : undefined);
          else toast.success(toastSpec.message);
        }
        clearActiveJob();
        setPublishDone(true);
      } catch (err) {
        // User-initiated cancel aborts the poller — onUserCancel already reset
        // the UI, so don't surface a false error.
        if ((err as { name?: string })?.name === 'AbortError') {
          clearActiveJob();
          return;
        }
        const reason = mapIgError((err as Error)?.message).friendly;
        setPublishError(reason);
        savePublishResult({
          kind: 'reel', state: 'error',
          accounts: publishStatuses.map(s => ({ id: s.account.id, name: s.account.name, profilePicture: s.account.profilePicture, status: s.status === 'success' ? 'success' as const : 'error' as const })),
          successCount: publishStatuses.filter(s => s.status === 'success').length, total: publishStatuses.length,
          errorReason: reason, ts: Date.now(),
        });
        clearActiveJob();
      } finally {
        setOverallStatus(null);
        setPublishing(false);
      }
    })();
    // Empty deps — run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Time-based progress creep during the Instagram-publishing wait: between
  // the moment the server hits the IG container step (bar at 90%) and the
  // first per-account success/error, real per-account events don't fire. To
  // keep the editor visually engaged, slowly raise the bar from 90% to 99%
  // over 120s. The 99% cap leaves 1% for the real `done` event so we never
  // show 100% before the publish is actually finished. `Math.max` in onEvent
  // ensures a real (lower) percent doesn't drag the creep back down.
  useEffect(() => {
    if (!overallStatus || overallStatus.phase !== 'publishing') return;
    if (overallStatus.percent >= 99) return;
    const startPercent = overallStatus.percent;
    const startedAt = Date.now();
    const DURATION_MS = 120_000;
    const TARGET = 99;
    const range = TARGET - startPercent;
    const id = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const targetPercent = Math.min(TARGET, startPercent + (elapsed / DURATION_MS) * range);
      setOverallStatus((prev) => {
        if (!prev || prev.phase !== 'publishing') return prev;
        const candidate = Math.round(targetPercent);
        return candidate > prev.percent ? { ...prev, percent: candidate } : prev;
      });
      if (elapsed >= DURATION_MS) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
    // Re-trigger only on phase transition into publishing; restarts from the
    // current percent. Subsequent setOverallStatus inside this effect would
    // otherwise re-fire the effect endlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overallStatus?.phase]);

  // Stall detection: if upload progress hasn't advanced for STALL_MS, show a
  // warning banner in the modal to prevent the user from closing the tab.
  useEffect(() => {
    if (!publishing) return;
    const id = setInterval(() => {
      if (isStalled(lastProgressRef.current, Date.now())) {
        setUploadWarning('Загрузка идёт медленно или зависла — держите вкладку открытой, не блокируйте экран.');
      }
    }, 5000);
    return () => clearInterval(id);
  }, [publishing]);

  // Авто-выбор первого аккаунта для превью/редактирования текста (без авто-чека)
  useEffect(() => {
    if (sortedAccounts.length === 0) return;
    const isSelectedValid = selectedAccountId && sortedAccounts.some(acc => acc.id === selectedAccountId);
    if (!isSelectedValid) {
      setSelectedAccountId(sortedAccounts[0].id);
    }
  }, [sortedAccounts, selectedAccountId]);

  // Пересчитываем publishStatuses при изменении checkedAccountIds
  useEffect(() => {
    if (!showPublishModal) return; // Только когда модалка открыта
    if (publishing) return; // Не сбрасывать статусы во время публикации
    // publishing флипается true→false в конце публикации и заново гоняет этот
    // эффект → он перетирал финальные success/error обратно в 'pending' (плитка
    // не загоралась зелёным). После завершения (publishDone) статусы не трогаем —
    // их сбросит handleAckDone при закрытии модалки.
    if (publishDone) return;

    // Только отмеченные: в аккаунт превью без галочки не публикуем.
    const statuses: AccountPublishStatus[] = accountsToPublish(activeAccounts, checkedAccountIds).map(account => ({
      account,
      status: 'pending' as const,
      caption: captions.get(account.id) || '',
    }));

    setPublishStatuses(statuses);
  }, [checkedAccountIds, activeAccounts, captions, showPublishModal, publishing, publishDone]);

  // Черновику экрана — только факт «видео есть»: для подтверждения смены формата и ухода.
  useEffect(() => { setDraftHasMedia(!!videoFile); }, [videoFile, setDraftHasMedia]);
  useEffect(() => () => setDraftHasMedia(false), [setDraftHasMedia]);

  // Очищаем blob URLs при размонтировании или смене файла
  useEffect(() => {
    return () => {
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
      if (introImagePreviewUrl) {
        URL.revokeObjectURL(introImagePreviewUrl);
      }
    };
  }, [videoPreviewUrl, introImagePreviewUrl]);

  // Инвалидируем in-flight проверку авторских прав при размонтировании
  useEffect(() => {
    return () => {
      copyrightCheckIdRef.current++;
    };
  }, []);

  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && !acceptVideo(file)) e.target.value = ''; // Очищаем input
  };

  /** Принять ролик в форму — из выбора файла или из «Монтажа». false — не годится. */
  const acceptVideo = (file: File): boolean => {
    if (file && file.type.startsWith('video/')) {
      setCopyrightStatus('idle');
      // Проверка размера файла (4GB для рилсов, как в Instagram)
      const MAX_FILE_SIZE = 4 * 1024 * 1024 * 1024; // 4GB
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Размер видео превышает 4GB (ваш файл: ${(file.size / 1024 / 1024 / 1024).toFixed(2)}GB). Это лимит Instagram для рилсов.`);
        return false;
      }

      setVideoFile(file);
      setFontSizes(new Map());
      const objectUrl = URL.createObjectURL(file);
      setVideoPreviewUrl(objectUrl);

      // Probe duration via a throwaway <video> element so we can show an estimate
      // in the publish modal before the user clicks. Failure is silent — estimate
      // simply won't render.
      setVideoDurationSec(null);
      const probe = document.createElement('video');
      probe.preload = 'metadata';
      probe.muted = true;
      probe.onloadedmetadata = () => {
        if (isFinite(probe.duration) && probe.duration > 0) setVideoDurationSec(probe.duration);
      };
      probe.src = objectUrl;

      setCopyrightStatus('checking');
      const checkId = ++copyrightCheckIdRef.current;
      copyrightApi.check(file).then((result) => {
        if (checkId === copyrightCheckIdRef.current) {
          setCopyrightStatus(result.status);
        }
      }).catch(() => {
        if (checkId === copyrightCheckIdRef.current) {
          setCopyrightStatus('error');
        }
      });
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!montageFrom) return;
    let cancelled = false;
    setMontageLoading(true);
    // Ролик из «Монтажа» уже смонтирован — по умолчанию без шаблона (08.10.2026).
    // У «Роликов B2G» Редакция кладёт свой шаблон — его не трогаем.
    if (!takeItem) setTemplateMode('without');
    (async () => {
      try {
        const source = await api.get<{ url: string; fileName: string; title: string; accountIds: string[] }>(
          `${montageFrom}/source`,
        );
        if (cancelled) return;
        setMontageAccountIds(source.accountIds);
        setMontageTitle(source.title);
        setMontageSourceReady(true);
        const response = await fetch(source.url);
        if (!response.ok) throw new Error(`Хранилище ответило ${response.status}`);
        const blob = await response.blob();
        if (cancelled) return;
        const name = /\.[a-z0-9]+$/i.test(source.fileName) ? source.fileName : `${source.fileName || 'reel'}.mp4`;
        acceptVideo(new File([blob], name, { type: blob.type.startsWith('video/') ? blob.type : 'video/mp4' }));
      } catch (error) {
        if (!cancelled) toast.error(`Не удалось взять ролик из «Монтажа»: ${error instanceof Error ? error.message : 'ошибка'}`);
      } finally {
        if (!cancelled) setMontageLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [montageFrom]);

  // Ролик из «Монтажа»: отмечен только его аккаунт (useDraftAccounts) — и он же в превью.
  useEffect(() => {
    const own = montageItemAccount.accountId;
    if (own && activeAccounts.some((a) => a.id === own)) setSelectedAccountId(own);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [montageItemAccount.accountId, accounts]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setLogoFile(file);
    }
  };

  const handleFontSizeChange = (delta: number) => {
    if (!selectedAccountId) return;
    const current = fontSizes.get(selectedAccountId) ?? textPosition?.fontSize ?? DEFAULT_PREVIEW_FONT_SIZE;
    haptic();
    const next = Math.min(200, Math.max(12, Math.round(current) + delta));
    setFontSizes(prev => new Map(prev).set(selectedAccountId, next));
  };

  const handleIntroImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setIntroImageFile(file);
      setIntroImagePreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleVideoUploadClick = () => {
    videoInputRef.current?.click();
  };

  const handleLogoUploadClick = () => {
    logoInputRef.current?.click();
  };

  const handleIntroImageUploadClick = () => {
    introImageInputRef.current?.click();
  };

  // Автоматически загружаем логотип из шаблона при выборе аккаунта (только в режиме "С шаблоном")
  useEffect(() => {
    if (templateMode === 'with' && selectedTemplate?.logoUrl) {
      // Конвертируем URL в File объект
      fetch(selectedTemplate.logoUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'template-logo.png', { type: blob.type });
          setLogoFile(file);
        })
        .catch(err => {
          console.error('Ошибка загрузки логотипа из шаблона:', err);
        });
    } else if (templateMode === 'with' && selectedTemplate && !selectedTemplate.logoUrl) {
      // Если выбран шаблон без логотипа, очищаем
      setLogoFile(null);
    }
  }, [selectedTemplate?.logoUrl, templateMode]);

  // Сбрасываем лого и текст при переключении режима
  useEffect(() => {
    if (templateMode === 'without') {
      setLogoFile(null);
      setIntroImageFile(null);
      setTextPosition(null);
      setTitles(new Map());
    } else {
      setTitles(new Map());
    }
  }, [templateMode]);

  // Логик публикации
  // Чего не хватает до публикации — и для строки под кнопкой, и для тоста.
  const checkedActive = accountsToPublish(activeAccounts, checkedAccountIds);
  const missing = missingForReel({
    checkedCount: checkedActive.length,
    hasVideo: !!videoFile,
    hasCategory: !!publicationCategory,
    needsTextPosition: !!videoFile && templateMode === 'with' && !textPosition,
  });
  const publishLabel = publishActionLabel({ count: checkedActive.length, scheduledAt: parseScheduled(scheduledAt) });

  /** Показать, чего нет: тост со списком и прокрутка к первому полю. */
  const pointToMissing = (list: MissingField[]) => {
    toast.error(missingText(list));
    const first = list[0];
    if (first.anchor === 'publish-media') setPreviewOpen(true);
    // Превью могло только что раскрыться — даём ему кадр на отрисовку.
    requestAnimationFrame(() => document.getElementById(first.anchor)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  const handleOpenPublishModal = async () => {
    if (missing.length > 0) {
      pointToMissing(missing);
      return;
    }

    // Только отмеченные аккаунты (из всех активных, не только отфильтрованных).
    const statuses: AccountPublishStatus[] = checkedActive.map(account => ({
      account,
      status: 'pending' as const,
      caption: captions.get(account.id) || '',
    }));

    setPublishStatuses(statuses);
    setShowPublishModal(true);
  };

  // Считаем общий % прогресса по всем фазам
  // С шаблоном: processing(N) + uploading(N) + publishing(N) = 3N шагов
  // Без шаблона: uploading(1) + publishing(N) = N+1 шагов
  const calcOverallPercent = (
    phase: 'processing' | 'uploading' | 'remuxing' | 'publishing',
    current: number, // 1-based
    total: number,
    withTemplate: boolean,
    progress = 0,
  ): number => {
    if (withTemplate) {
      const steps = 3 * total;
      if (phase === 'processing') return Math.min(99, Math.round(((current - 1 + progress / 100) / steps) * 100));
      if (phase === 'uploading')  return Math.min(99, Math.round(((total + current - 0.5) / steps) * 100));
      return Math.min(99, Math.round(((2 * total + current - 0.5) / steps) * 100));
    } else {
      const steps = 1 + total;
      if (phase === 'uploading')  return Math.min(49, Math.round((0.5 / steps) * 100));
      return Math.min(99, Math.round(((current + 0.5) / steps) * 100));
    }
  };

  // Второе касание «Опубликовать», пока первое ещё ждёт проверку бандла, — до
  // того как `publishing` успел стать true, — запускало публикацию дважды
  // (22.10.2026 karaganda.today09: два рилса с разницей 82 мс; с 10.10 — шесть
  // таких случаев). Флаг в ref ставится синхронно, на том же касании.
  const publishStartRef = useRef(false);
  const handleConfirmPublish = async () => {
    if (publishStartRef.current) return;
    publishStartRef.current = true;
    try {
      await confirmPublishOnce();
    } finally {
      publishStartRef.current = false;
    }
  };

  const confirmPublishOnce = async () => {
    // Вкладка со старым бандлом рендерит по старым правилам (отзыв 04.09) —
    // публиковать из неё нельзя, только после перезагрузки.
    if (await isBundleStale()) {
      toast.error(STALE_BUNDLE_MESSAGE);
      return;
    }

    // Capability guard: skip accounts that cannot publish via the selected mode.
    const incompatible = publishStatuses.filter(s => !canPublishVia(s.account, publishMode));
    if (incompatible.length > 0) {
      toast.warning(`Пропущены (нельзя через ${publishMode === 'phone' ? 'телефон' : 'API'}): ${incompatible.map(s => '@' + (s.account.username || s.account.name)).join(', ')}`);
      // All selected accounts are incompatible → nothing to publish.
      if (incompatible.length === publishStatuses.length) return;
    }

    setPublishing(true);

    // Генерируем batchId для группировки публикаций
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setLastBatchId(batchId);

    const total = publishStatuses.length;
    console.log('🚀 Publishing to accounts:', publishStatuses.map((s: AccountPublishStatus) => s.account.name), 'total:', total);

    // Helper: Run server pipeline via SSE
    const runServerPipeline = async (): Promise<PublishResult[]> => {
      const accountsForServer = publishStatuses.map(s => ({ id: s.account.id, name: s.account.name, igUsername: s.account.username || s.account.name }));

      // Build render tasks. With template: group accounts by unique overlay hash.
      // Without template: a single task with no overlay, shared by all accounts.
      const renderTasks =
        templateMode === 'with' && textPosition
          ? buildRenderTasks({
              accounts: publishStatuses.map(({ account }) => ({ id: account.id })),
              templates,
              titles,
              textPosition: {
                x: textPosition.x,
                y: textPosition.y,
                previewWidth: textPosition.previewWidth,
                previewHeight: textPosition.previewHeight,
                textBoxWidth: textPosition.textBoxWidth,
                // Forward the bottom anchor so buildRenderTasks wraps with the
                // real box position (notch two-pass) and the backend renderer
                // bottom-anchors the box. Per-account `lines` are recomputed in
                // buildRenderTasks (titles differ per account) — not forwarded here.
                bottom: textPosition.bottom,
              },
              fontSizes,
              // Выбор «заголовок 3/5/10 с» — сервер уберёт текст после этого времени.
              textDuration,
            })
          : [buildPlainRenderTask(publishStatuses.map(({ account }) => ({ id: account.id })))];

      const captionsRecord: Record<string, string> = {};
      publishStatuses.forEach(s => {
        captionsRecord[s.account.id] = s.caption || captions.get(s.account.id) || '';
      });

      const results: PublishResult[] = [];
      let serverResults: { accountId: string; status: 'success' | 'error'; instagramMediaId?: string; error?: string }[] = [];

      // Progress budget: see serverProgress.ts (upload 0..40, render 40..90, publish 90..100).
      let publishedCount = 0;
      const jobId = crypto.randomUUID();
      const publishAbort = new AbortController();
      publishAbortRef.current = publishAbort;
      // Surface jobId to the modal so a user-initiated close can call the
      // backend cancel endpoint (POST /api/reels/:jobId/cancel).
      setCurrentJobId(jobId);
      saveActiveJob({
        jobId,
        batchId,
        accountIds: accountsForServer.map((a) => a.id),
        startedAt: Date.now(),
      });
      setOverallStatus({ phase: 'uploading', current: 1, total, percent: 0 });

      lastProgressRef.current = Date.now();
      setUploadWarning(bigFileWarning(videoFile?.size ?? 0));
      setRetryNotice(null);

      // Диагностика front→back: что фронт ШЛЁТ серверу по тени/обводке на аккаунт.
      // Сравнивать с бэк-логом '[reels] overlay IN'. См. при расхождениях превью/рендера.
      renderTasks.forEach((t) => t.overlay && console.log('[reels] overlay OUT', t.accountIds[0], {
        softShadow: t.overlay.textSoftShadowEnabled,
        softBlur: t.overlay.textSoftShadowBlur, offX: t.overlay.textSoftShadowOffsetX,
        offY: t.overlay.textSoftShadowOffsetY, softOpacity: t.overlay.textSoftShadowOpacity,
        outline: t.overlay.textShadowEnabled, outlineWidth: t.overlay.textShadowWidth,
        outlineOpacity: t.overlay.textShadowOpacity, fontSize: t.overlay.fontSize, textBoxStyle: t.overlay.textBoxStyle,
      }));

      const onServerEvent = (event: ReelJobEvent) => {
        lastProgressRef.current = Date.now();
        // Публикация ушла в очередь: рендер закончен, Instagram получит видео
        // в назначенное время — дальше по этой джобе событий не будет.
        if (event.phase === 'scheduled') {
          const at = new Date(event.runAt).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
          // Публикации не было — плитки не должны остаться крутящимися.
          setPublishStatuses(prev => prev.map(s => ({ ...s, status: 'scheduled' as const, stage: undefined })));
          setScheduledNotice(`Публикация запланирована на ${at} · аккаунтов: ${event.accounts}`);
          scheduledRef.current = true;
          toast.success(`Запланировано на ${at} · аккаунтов: ${event.accounts}`);
          return;
        }
        // Per-account publish events also drive list status + error reporting.
        if (event.phase === 'publish' && event.accountId) {
          const accId = event.accountId;
          // IG-side transient failure being auto-retried by the backend. Not a
          // terminal outcome: tell the user, flip the tile back to publishing,
          // and DON'T count it (publishedCount tracks terminal results) or
          // report it as an error.
          if (event.status === 'retrying') {
            // Show the retry notice inside the publish window (not a toast),
            // consistent with the slow-upload warning banner.
            setRetryNotice(retryNoticeMessage(event.attempt));
            setPublishStatuses(prev =>
              prev.map(s => s.account.id === accId ? { ...s, status: 'publishing' as const, stage: undefined } : s)
            );
          } else if (event.status === 'success' || event.status === 'error') {
            // Terminal per-account outcome — the only thing that advances the
            // publish counter. (The phone path also streams non-terminal
            // `stage` events with accountId but no status; those must NOT count.)
            publishedCount += 1;
            setRetryNotice(null);
            // Match by account id INSIDE the functional updater. The captured
            // `publishStatuses` closure is stale (set just before the pipeline
            // ran, not yet reflected here), so findIndex on it returns -1 on the
            // first publish and the tile never flips to green/red.
            if (event.status === 'success') {
              setPublishStatuses(prev =>
                prev.map(s => s.account.id === accId ? { ...s, status: 'success' as const, stage: undefined } : s)
              );
            } else {
              const rawMsg = event.error || 'Unknown error';
              setPublishStatuses(prev =>
                prev.map(s => s.account.id === accId ? { ...s, status: 'error' as const, error: mapIgError(rawMsg).friendly, stage: undefined } : s)
              );
              reportError(new Error(rawMsg), { phase: 'publish', accountId: accId, batchId });
            }
          } else if (event.stage) {
            // Phone-publish granular stage (no terminal status): keep the tile
            // 'publishing' and surface the live stage for the user.
            setPublishStatuses(prev =>
              prev.map(s => s.account.id === accId ? { ...s, status: 'publishing' as const, stage: event.stage } : s)
            );
          }
        }
        if (event.phase === 'done') {
          serverResults = event.results || [];
          // Рилс уже в ленте; сторис, которые Instagram не принял, — отдельным предупреждением.
          const storyFail = describeStoryFailures(serverResults.map((r) => {
            const acc = activeAccounts.find((a) => a.id === r.accountId);
            return { name: acc?.username || acc?.name || r.accountId, story: r.story };
          }));
          if (storyFail) toast.warning('Сторис вышли не везде', { description: storyFail, duration: 15000 });
        }
        if (event.phase === 'error') {
          throw new Error(`Server pipeline error: ${event.error}`);
        }
        const next = serverEventToStatus(event, { total, publishedCount });
        if (next) {
          // Never regress percent — a slow creep (time-based) may have already
          // raised the bar above what this event reports.
          setOverallStatus((prev) =>
            prev ? { ...next, percent: Math.max(prev.percent, next.percent) } : next,
          );
        }
      };

      // Тот же ролик уже публикуется (кнопка вернулась раньше, чем вышел рилс,
      // и её нажали снова) — второй раз не грузим, а показываем ход той задачи.
      const duplicateOf = await claimReelJob({
        jobId,
        accounts: accountsForServer,
        captions: captionsRecord,
        fileSize: videoFile!.size,
        scheduledAt: fromDateTimeLocal(scheduledAt) ?? undefined,
      });
      if (duplicateOf) {
        toast.info('Этот ролик уже публикуется в эти аккаунты — показываем, как идёт. Второй раз не отправляем.');
        setCurrentJobId(duplicateOf);
        saveActiveJob({ jobId: duplicateOf, batchId, accountIds: accountsForServer.map((a) => a.id), startedAt: Date.now() });
        setOverallStatus({ phase: 'publishing', current: 1, total, percent: 0 });
        const failure: { error?: string } = {};
        await pollReelJobStatus(duplicateOf, {
          signal: publishAbort.signal,
          timeoutMs: 60 * 60_000,
          // Ошибку поднимаем после опроса: брошенная из onEvent, она оборвала бы опрос молча.
          onEvent: (event) => {
            if (event.phase === 'error') { failure.error = event.error; return; }
            onServerEvent(event);
          },
        });
        if (failure.error) throw new Error(`Server pipeline error: ${failure.error}`);
      } else {
        await submitReelJobServer({
          file: videoFile!,
          signal: publishAbort.signal,
          meta: {
            batchId,
            jobId,
            accounts: accountsForServer,
            captions: captionsRecord,
            renderTasks,
            duplicateToStory,
            publishMode,
            ...(publicationCategory ? { category: publicationCategory } : {}),
            ...(fromDateTimeLocal(scheduledAt) ? { scheduledAt: fromDateTimeLocal(scheduledAt) as string } : {}),
            ...(collaborators.length > 0 ? { collaborators } : {}),
            ...(music ? { music: { url: music.url, volume: music.volume, startSec: music.startSec } } : {}),
            fileSize: videoFile!.size,
          },
          onUploadProgress: (percent) => {
            lastProgressRef.current = Date.now();
            setOverallStatus(uploadProgressToStatus(percent, total));
          },
          onEvent: onServerEvent,
        });
      }

      // Map server results to PublishResult format
      serverResults.forEach((sr) => {
        const idx = publishStatuses.findIndex(s => s.account.id === sr.accountId);
        results.push({
          index: idx >= 0 ? idx : 0,
          status: sr.status,
          error: sr.error ? mapIgError(sr.error).friendly : undefined,
        });
      });

      // Прогрев общего кэша отчёта: заголовок с кадра известен прямо сейчас —
      // отчёту не придётся потом распознавать его с превью через GPT.
      // Пишем то, что реально напечатано (с учётом капса шаблона), с той же
      // нормализацией пробелов, что у распознанных тем: ручные переносы
      // строк заголовка в отчёте становятся одиночными пробелами.
      const themeWrites = serverResults.flatMap((sr) => {
        if (sr.status !== 'success' || !sr.instagramMediaId) return [];
        const title = (titles.get(sr.accountId) ?? '').replace(/\s+/g, ' ').trim();
        if (!title) return [];
        const t = templates.find(tpl => tpl.accountId === sr.accountId && (tpl.type ?? 'reel') === 'reel');
        return [{ id: sr.instagramMediaId, theme: t?.textUppercase ? title.toUpperCase() : title }];
      });
      void saveEnrichThemes(themeWrites);

      const posted = serverResults.filter((sr) => sr.status === 'success' && sr.instagramMediaId);
      if (posted.length) saveLastAccounts(userId, 'reel', posted.map((sr) => sr.accountId));
      // Ролик из «Монтажа»: ссылку на пост прикладываем сами — ролик закрыт.
      if (montageFrom && !takeItem) {
        const first = posted.find((sr) => montageAccountIds.includes(sr.accountId)) ?? posted[0];
        if (first) {
          api.post(`${montageFrom}/published-media`, {
            accountId: first.accountId, mediaId: first.instagramMediaId,
          }).then(() => toast.success('Ссылка на пост приложена к ролику в «Монтаже»'))
            .catch((e) => toast.error(`Ссылку в «Монтаж» приложите руками: ${e instanceof Error ? e.message : 'ошибка'}`));
        }
      } else if (posted.length) {
        // «Ролики B2G» и свои ролики раздающей: ссылки всех пабликов — в учёт.
        const firstId = posted[0].accountId;
        reportPublished({
          takeItem,
          posts: posted.map((sr) => ({ accountId: sr.accountId, mediaId: sr.instagramMediaId as string })),
          withBrief: logsWork ? withBrief : undefined,
          logsWork,
          title: montageTitle || workTitleFrom(titles.get(firstId) ?? '', captions.get(firstId) ?? ''),
        }).then((kind) => {
          if (kind === 'take') toast.success(`Отмечено в «Роликах B2G» — взяла, ссылок: ${posted.length}`);
          if (kind === 'work') toast.success(`Записано в «Отработано» — ссылок: ${posted.length}`);
        }).catch((e) => toast.error(`В «Отработано» не записалось — добавьте руками: ${e instanceof Error ? e.message : 'ошибка'}`));
      }

      // Authoritative final reconciliation of tile colors from the `done`
      // results — live per-account events can be missed/dropped (or matched
      // against a stale closure), leaving tiles stuck on 'pending' while the
      // header already says "Все опубликованы". Keyed by account id.
      if (serverResults.length > 0) {
        setPublishStatuses(prev =>
          prev.map(s => {
            const sr = serverResults.find(r => r.accountId === s.account.id);
            if (!sr) return s;
            return sr.status === 'success'
              ? { ...s, status: 'success' as const }
              : { ...s, status: 'error' as const, error: sr.error ? mapIgError(sr.error).friendly : s.error };
          })
        );
      }

      return results;
    };

    // Helper: Run browser pipeline (existing logic)
    const runBrowserPipeline = async (): Promise<PublishResult[]> => {
      // Шаг 1: Обрабатываем и загружаем видео для каждого аккаунта
      const videoUrls = new Map<string, string>();
      let results: PublishResult[] = [];

      if (templateMode === 'without') {
        let sharedUploadMs = 0;
        let sharedRemuxMs: number | null = null;

        if (!videoUrls.has('original')) {
          setOverallStatus({ phase: 'uploading', current: 1, total, percent: 5 });

          let url: string;
          if (needsRemux(videoFile!)) {
            toast.info('Видео конвертируется для Instagram (~1-2 мин). Не закрывайте вкладку.', { duration: 7000 });
            const { result: uploaded, ms: upMs } = await measureAsync(() =>
              uploadApi.uploadReel(videoFile!, (pct) => {
                setOverallStatus({ phase: 'uploading', current: 1, total, percent: Math.round(pct / 2) });
              })
            );
            const { result: remuxed, ms: rxMs } = await measureAsync(() =>
              uploadApi.remuxToMp4(uploaded.url, videoFile!.name, (pct) => {
                setOverallStatus({ phase: 'remuxing', current: 1, total, percent: pct });
              })
            );
            url = remuxed.url;
            sharedUploadMs = upMs;
            sharedRemuxMs = rxMs;
          } else {
            const { result: uploaded, ms: upMs } = await measureAsync(() =>
              uploadApi.uploadReel(videoFile!, (pct) => {
                setOverallStatus({ phase: 'uploading', current: 1, total, percent: Math.min(49, Math.round(pct / 2)) });
              })
            );
            url = uploaded.url;
            sharedUploadMs = upMs;
          }
          videoUrls.set('original', url);
        }

        // Устанавливаем url для всех аккаунтов
        const originalUrl = videoUrls.get('original')!;
        publishStatuses.forEach(({ account }) => {
          videoUrls.set(account.id, originalUrl);
        });

        // Шаг 2: Создаём и публикуем рилсы параллельно для режима "без шаблона"
        setOverallStatus({ phase: 'publishing', current: 0, total, percent: 90 });

        const inputSizeMB = videoFile!.size / 1024 / 1024;
        results = await publishReelsParallel(
          {
            publishStatuses, videoUrls, captions, batchId, publicationCategory, duplicateToStory,
            onAccountTimed: (index, igTiming) => {
              const account = publishStatuses[index]?.account;
              if (!account) return;
              printAccountTiming({
                accountName: account.name,
                inputSizeMB,
                processedSizeMB: inputSizeMB,
                renderMs: null,
                supabaseUploadMs: sharedUploadMs,
                remuxMs: sharedRemuxMs,
                igCreateMs: igTiming.igCreateMs,
                igPublishMs: igTiming.igPublishMs,
                totalMs: sharedUploadMs + (sharedRemuxMs ?? 0) + igTiming.igCreateMs + igTiming.igPublishMs,
              });
            },
          },
          reelsApi,
          (completed, tot) => {
            const publishPercent = Math.min(99, Math.round(90 + (completed / tot) * 9));
            setOverallStatus({ phase: 'publishing', current: completed, total: tot, percent: publishPercent });
          },
        ).then(res => {
          res.forEach((result, index) => {
            if (result.status === 'success') {
              setPublishStatuses(prev =>
                prev.map((s, idx) => idx === index ? { ...s, status: 'success' as const } : s)
              );
            } else {
              setPublishStatuses(prev =>
                prev.map((s, idx) => idx === index ? { ...s, status: 'error' as const, error: result.error } : s)
              );
            }
          });
          res.forEach((result) => {
            if (result.status === 'error') {
              const account = publishStatuses[result.index]?.account;
              reportError(new Error(result.error), {
                phase: 'publish',
                accountId: account?.id,
                accountName: account?.name,
                batchId,
              });
            }
          });
          return res;
        });
      } else {
        // templateMode === 'with'
        setOverallStatus({ phase: 'processing', current: 0, total, progress: 0, percent: 0 });
        setIsProcessing(true);

        const instagramPublishPromises: Promise<PublishResult>[] = [];
        let completedCount = 0;

        // WebCodecs uses a single shared hook instance — process sequentially to avoid
        // worker ref collision. Legacy VideoProcessor path can run 2 in parallel safely.
        const CHUNK_SIZE = webCodecsSupported ? 1 : 2;
        
        for (let i = 0; i < publishStatuses.length; i += CHUNK_SIZE) {
          const chunk = publishStatuses.slice(i, i + CHUNK_SIZE);
          
          const processPromises = chunk.map(async ({ account }, chunkIndex) => {
            const globalIndex = i + chunkIndex;
            setPublishStatuses(prev =>
              prev.map((s, idx) => idx === globalIndex ? { ...s, status: 'publishing' as const } : s)
            );

            const accountTemplate = templates.find(t => t.accountId === account.id && (t.type ?? 'reel') === 'reel');
            let accountLogoFile: File | null = null;
            if (accountTemplate?.logoUrl) {
              try {
                const logoBlob = await fetch(accountTemplate.logoUrl).then(res => res.blob());
                accountLogoFile = new File([logoBlob], 'logo.png', { type: logoBlob.type });
              } catch (err) {
                reportError(err, { phase: 'logo_fetch', accountId: account.id, accountName: account.name });
                console.error('Ошибка загрузки логотипа для аккаунта:', err);
              }
            }

            let processedBlob: Blob;
            let renderMs: number;

            if (webCodecsSupported) {
              const logoArrayBuffer = accountLogoFile ? await accountLogoFile.arrayBuffer() : null;
              const overlayParams: OverlayParams | null = textPosition
                ? await buildOverlayParams({
                    template: accountTemplate ?? {},
                    text: titles.get(account.id) || '',
                    textPosition,
                    fontSize: fontSizes.get(account.id) ?? null,
                    logoArrayBuffer,
                  })
                : null;

              ({ result: processedBlob, ms: renderMs } = await measureAsync(() =>
                processWithWebCodecs({
                  videoFile: videoFile!,
                  overlay: overlayParams,
                  onProgress: (pct: number) => {
                    setOverallStatus(prev => ({
                      ...prev!,
                      phase: 'processing',
                      percent: calcOverallPercent('processing', globalIndex + 1, total, true, pct),
                    }));
                  },
                })
              ));
            } else {
              ({ result: processedBlob, ms: renderMs } = await measureAsync(() =>
                new Promise<Blob>((resolve, reject) => {
                  setProcessingQueue(prev => [...prev, {
                    accountId: account.id,
                    logoFile: accountLogoFile,
                    resolve,
                    reject,
                  }]);
                })
              ));
            }

            const processedVideoFile = new File(
              [processedBlob],
              videoFile!.name.replace(/\.[^/.]+$/, `-${account.id}.mp4`),
              { type: 'video/mp4' }
            );

            console.log(`📹 Processed video for account ${account.name}:`, {
              originalSize: `${(videoFile!.size / 1024 / 1024).toFixed(2)} MB`,
              processedSize: `${(processedVideoFile.size / 1024 / 1024).toFixed(2)} MB`,
            });

            // Upload
            setOverallStatus(prev => {
              const current = (prev?.current || 0) + 1;
              return { phase: 'uploading', current, total, percent: calcOverallPercent('uploading', current, total, true) };
            });
            
            let url: string;
            let supabaseUploadMs: number;
            let remuxMs: number | null = null;

            if (processedBlobNeedsRemux(processedBlob)) {
              if (!videoUrls.size) {
                toast.info('Видео конвертируется для Instagram (~1-2 мин). Не закрывайте вкладку.', { duration: 7000 });
              }
              const { result: uploaded, ms: upMs } = await measureAsync(() => uploadApi.uploadReel(processedVideoFile));
              const { result: remuxed, ms: rxMs } = await measureAsync(() => uploadApi.remuxToMp4(uploaded.url, processedVideoFile.name));
              url = remuxed.url;
              supabaseUploadMs = upMs;
              remuxMs = rxMs;
            } else {
              const { result: uploaded, ms: upMs } = await measureAsync(() => uploadApi.uploadReel(processedVideoFile));
              url = uploaded.url;
              supabaseUploadMs = upMs;
            }
            videoUrls.set(account.id, url);

            const capturedRenderMs = renderMs;
            const capturedUploadMs = supabaseUploadMs;
            const capturedRemuxMs = remuxMs;
            const inputSizeMB = videoFile!.size / 1024 / 1024;
            const processedSizeMB = processedBlob.size / 1024 / 1024;

            // Pipeline: Сразу после загрузки отправляем команду на публикацию в Instagram!
            // Не ждем (не делаем await здесь), чтобы следующий чанк мог начать рендеринг.
            const publishPromise = (async (): Promise<PublishResult> => {
              try {
                const { result: createResult, ms: igCreateMs } = await measureAsync(() =>
                  reelsApi.create({
                    accountId: account.id,
                    media: { url },
                    caption: captions.get(account.id) ?? '',
                    batchId,
                    category: publicationCategory,
                    ...(collaborators.length > 0 ? { collaborators } : {}),
                  })
                );
                const { reel } = createResult;

                const { ms: igPublishMs } = await measureAsync(() =>
                  reelsApi.publish(reel.id, { duplicateToStory })
                );

                printAccountTiming({
                  accountName: account.name,
                  inputSizeMB,
                  processedSizeMB,
                  renderMs: capturedRenderMs,
                  supabaseUploadMs: capturedUploadMs,
                  remuxMs: capturedRemuxMs,
                  igCreateMs,
                  igPublishMs,
                  totalMs: capturedRenderMs + capturedUploadMs + (capturedRemuxMs ?? 0) + igCreateMs + igPublishMs,
                });

                completedCount++;
                const publishPercent = calcOverallPercent('publishing', completedCount, total, true);
                setOverallStatus(prev => prev ? { ...prev, phase: 'publishing', percent: publishPercent } : prev);

                setPublishStatuses(prev => prev.map((s, idx) => idx === globalIndex ? { ...s, status: 'success' as const } : s));
                return { index: globalIndex, status: 'success' as const };
              } catch (err) {
                reportError(err, {
                  phase: 'publish',
                  accountId: account.id,
                  accountName: account.name,
                  batchId,
                });
                const errorStr = mapIgError(err instanceof Error ? err.message : 'Неизвестная ошибка').friendly;
                setPublishStatuses(prev => prev.map((s, idx) => idx === globalIndex ? { ...s, status: 'error' as const, error: errorStr } : s));
                return { index: globalIndex, status: 'error' as const, error: errorStr };
              }
            })();

            instagramPublishPromises.push(publishPromise);
          });

          // Ждем завершения рендеринга и загрузки (но НЕ ждем публикации в Instagram!)
          await Promise.all(processPromises);
        }
        
        setIsProcessing(false);
        setProcessingQueue([]);

        // Ждем завершения всех фоновых публикаций в Instagram
        results = await Promise.all(instagramPublishPromises);
      }

      return results;
    };

    try {
      // Try server pipeline first
      console.log('🚀 Attempting server-side reels pipeline');
      const results = await runServerPipeline();

      const toastSpec = resolvePublishToast(results);
      sendPublishTelemetry({
        site: 'runServerPipeline',
        outcome: 'resolved',
        toastVariant: toastSpec.variant,
        resultsCount: results.length,
        statuses: results.map((r) => r.status),
      });
      clearActiveJob();
      const succ = results.filter(r => r.status === 'success').length;
      const allFailed = succ === 0 && results.length > 0;
      const firstErr = mapIgError(results.find(r => r.status === 'error')?.error).friendly;
      // Ушло в очередь — про это уже сказал тост «Запланировано», общий итог
      // публикации тут неуместен (публиковать ещё нечего).
      if (!allFailed && !scheduledRef.current) {
        // toast only for partial/success (NOT full failure)
        if (toastSpec.variant === 'warning') toast.warning(toastSpec.message, ('description' in toastSpec && toastSpec.description) ? { description: toastSpec.description } : undefined);
        else if (toastSpec.variant === 'success') toast.success(toastSpec.message);
      }
      setPublishStatuses(prev => {
        savePublishResult({
          kind: 'reel', state: allFailed ? 'error' : 'done',
          accounts: prev.map(s => ({
            id: s.account.id, name: s.account.name, profilePicture: s.account.profilePicture,
            status: s.status === 'success' ? 'success' as const : 'error' as const,
            friendly: s.status === 'error' ? s.error : undefined, raw: undefined,
          })),
          successCount: prev.filter(s => s.status === 'success').length, total: prev.length,
          errorReason: allFailed ? firstErr : undefined, ts: Date.now(),
        });
        return prev;
      });
      if (allFailed) setPublishError(firstErr); else setPublishDone(true);
    } catch (err) {
      const errorName = (err as Error)?.name;
      const errorMessage = (err as Error)?.message;
      const errorCode = (err as { code?: string })?.code;
      sendPublishTelemetry({
        site: 'runServerPipeline',
        outcome: err instanceof UploadFailedError ? 'upload-failed' : err instanceof JobLostError ? 'job-lost' : 'error',
        errorName,
        errorMessage,
        errorCode,
      });
      if (err instanceof UploadFailedError) {
        const reason = mapIgError(err.message).friendly;
        // NO toast — upload errors must be shown in the modal (toast missed on
        // mobile / when tab was backgrounded). Modal stays until OK.
        setPublishError(reason);
        savePublishResult({
          kind: 'reel', state: 'error',
          accounts: publishStatuses.map(s => ({ id: s.account.id, name: s.account.name, profilePicture: s.account.profilePicture, status: s.status === 'success' ? 'success' as const : 'error' as const })),
          successCount: publishStatuses.filter(s => s.status === 'success').length,
          total: publishStatuses.length, errorReason: reason, ts: Date.now(),
        });
        clearActiveJob();
      } else if (err instanceof JobLostError) {
        toast.info('Соединение прервано. Публикация продолжается в фоне — проверьте Instagram через несколько минут.');
        clearActiveJob();
      } else if (/cancelled-by-user/i.test(errorMessage ?? '') || errorName === 'AbortError') {
        // User-initiated cancel: onUserCancel already showed 'Публикация отменена'
        // and tore down local state. Don't surface a second error toast or
        // Sentry record for what's a normal outcome.
        clearActiveJob();
      } else if ((err as { status?: number })?.status === 409) {
        // Бэкенд узнал в загрузке тот же ролик, что уже идёт или вышел, и второй
        // раз его не публикует (проверку перед загрузкой обошла гонка).
        toast.info('Этот ролик уже публикуется или опубликован в эти аккаунты — второй раз не отправляли.');
        clearActiveJob();
      } else {
        // Browser fallback temporarily disabled — surface server errors directly
        Sentry.captureException(err, { tags: { phase: 'publish_critical', pipeline: 'server' } });
        console.error('Критическая ошибка при публикации (server pipeline):', err);
        toast.error('Ошибка при обработке видео');
        clearActiveJob();
      }
    }

    setOverallStatus(null);
    setPublishing(false);
    setCurrentJobId(null);
  };

  const handleAccountCheck = (accountId: string, checked: boolean) => {
    setCheckedAccountIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(accountId);
      } else {
        newSet.delete(accountId);
      }
      return newSet;
    });
  };

  /** «Выбрать все по фильтру» / «Снять все». */
  const handleCheckMany = (ids: string[], checked: boolean) => {
    setCheckedAccountIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const handleAckDone = () => {
    setPublishDone(false);
    setScheduledNotice(null);
    scheduledRef.current = false;
    setPublishError(null);
    setUploadWarning(null);
    setRetryNotice(null);
    clearPublishResult();
    setShowPublishModal(false);
    setPublishStatuses([]);
    setLastBatchId(null);
    setSelectedAccountId('');
    // Галочки остаются: следующая публикация чаще всего в те же паблики
    // («Ещё одну в эти же аккаунты»). Отмеченный набор уже стал «последним».
    setCaptions(new Map());
    setScheduledAt('');
    setVideoFile(null);
    setVideoDurationSec(null);
    if (videoInputRef.current) videoInputRef.current.value = '';
    setFontSizes(new Map());
    setLogoFile(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
    setTextPosition(null);
    setTitles(new Map());
    setPublicationCategory(defaultCategory(!!takeItem));
    setWithBrief(true);
    refreshLast();
    setTextScope(null);
    setCollaborators([]);
  };

  const handleRetry = () => {
    setPublishError(null);
    setUploadWarning(null);
    setRetryNotice(null);
    clearPublishResult();
    setPublishStatuses(prev => prev.map(s => ({ ...s, status: 'pending' as const, error: undefined })));
    handleConfirmPublish();
  };

  // Синхронизируем состояние публикации с store для кнопки в хедере. Кнопка
  // не гаснет молча: нажатие показывает, чего не хватает. Обработчик — через
  // ref, иначе в шапке остался бы снимок формы с прошлого рендера.
  const openPublishRef = useRef(handleOpenPublishModal);
  openPublishRef.current = handleOpenPublishModal;
  const hint = missingText(missing);
  useEffect(() => {
    const currentLoading = loading || isProcessing;
    setPublishState({
      disabled: currentLoading || publishing,
      loading: currentLoading || publishing,
      handlePublish: () => { void openPublishRef.current(); },
      label: publishLabel,
      hint,
    });
  }, [loading, isProcessing, publishing, publishLabel, hint, setPublishState]);

  // Очищаем store при размонтировании
  useEffect(() => {
    return () => {
      resetPublishState();
    };
  }, [resetPublishState]);

  // Получаем описание для выбранного аккаунта
  const description = selectedAccountId ? (captions.get(selectedAccountId) || '') : '';

  // Аккаунты для генерации - только когда есть выбранные чекбоксы
  const accountsForGeneration = checkedAccountIds.size > 0 
    ? filteredAccounts.filter(acc => checkedAccountIds.has(acc.id)).map(acc => ({ id: acc.id, name: acc.name }))
    : [];

  const handleCaptionsGenerated = (generatedCaptions: Map<string, string>) => {
    // Мерджим с существующими описаниями (на случай если в Map были другие аккаунты)
    const newCaptions = new Map(captions);
    generatedCaptions.forEach((caption, accountId) => {
      newCaptions.set(accountId, caption);
    });
    setCaptions(newCaptions);
    console.log('📝 Captions updated:', Object.fromEntries(newCaptions));
  };

  const handleTitlesGenerated = (generatedTitles: Map<string, string>) => {
    // Мерджим с существующими заголовками
    const newTitles = new Map(titles);
    generatedTitles.forEach((title, accountId) => {
      newTitles.set(accountId, title);
    });
    setTitles(newTitles);
    console.log('📝 Titles updated:', Object.fromEntries(newTitles));
  };

  // Обработчик изменения заголовка
  const handleTitleChange = (newValue: string) => {
    if (!selectedAccountId) return;

    const scoped = scopeTargets(accountGroups, textScope, checkedAccountIds, selectedAccountId);
    if (scoped) {
      setTitles(prev => { const next = new Map(prev); scoped.forEach(id => next.set(id, newValue)); return next; });
      return;
    }

    // Получаем все значения заголовков активных аккаунтов
    const activeAccountValues = activeAccounts
      .map(acc => titles.get(acc.id) || '')
      .filter((val, idx, arr) => arr.indexOf(val) === idx); // Уникальные значения

    // Если у всех одинаковое значение (или Map пуста) - меняем у всех
    const allSame = activeAccountValues.length <= 1;

    if (allSame) {
      // Применяем ко всем активным аккаунтам
      const newTitles = new Map(titles);
      activeAccounts.forEach(acc => {
        newTitles.set(acc.id, newValue);
      });
      setTitles(newTitles);
    } else {
      // Меняем только у текущего аккаунта
      setTitles(prev => new Map(prev).set(selectedAccountId, newValue));
    }
  };

  // Обработчик изменения описания
  const handleDescriptionChange = (newValue: string) => {
    if (!selectedAccountId) return;

    const scoped = scopeTargets(accountGroups, textScope, checkedAccountIds, selectedAccountId);
    if (scoped) {
      setCaptions(prev => { const next = new Map(prev); scoped.forEach(id => next.set(id, newValue)); return next; });
      return;
    }

    // Получаем все значения описаний активных аккаунтов
    const activeAccountValues = activeAccounts
      .map(acc => captions.get(acc.id) || '')
      .filter((val, idx, arr) => arr.indexOf(val) === idx); // Уникальные значения

    // Если у всех одинаковое значение (или Map пуста) - меняем у всех
    const allSame = activeAccountValues.length <= 1;

    if (allSame) {
      // Применяем ко всем активным аккаунтам
      const newCaptions = new Map(captions);
      activeAccounts.forEach(acc => {
        newCaptions.set(acc.id, newValue);
      });
      setCaptions(newCaptions);
    } else {
      // Меняем только у текущего аккаунта
      setCaptions(prev => new Map(prev).set(selectedAccountId, newValue));
    }
  };

  const handleProcessingComplete = (blob: Blob) => {
    setProcessedBlob(blob);
    setIsProcessing(false);
    toast.success('Видео успешно обработано!');
  };

  const handleProcessingError = (error: string) => {
    console.error('Processing error:', error);
    setIsProcessing(false);
    toast.error(`Ошибка обработки видео: ${error}`);
  };

  // Превью одно на форму: справа на широком экране, в потоке под аккаунтами —
  // на узком. Два экземпляра сразу нельзя: превью пишет позицию текста в форму.
  const previewContent = (
    <div id="publish-media" className="space-y-2">
          {templateMode === 'without' ? (
            // Режим "Без шаблона" - простое видео
            <div className="aspect-[9/16] bg-muted rounded-lg overflow-hidden flex items-center justify-center">
              {videoFile ? (
                <video
                  src={videoPreviewUrl || ''}
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  loop
                  muted
                />
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleVideoUploadClick}
                  className="h-full w-full flex-col gap-2 text-muted-foreground"
                >
                  <Video className="size-12" />
                  Загрузить видео
                </Button>
              )}
            </div>
          ) : (
            // Режим "С шаблоном" - полноценное превью
            <ReelPreview
              text={titles.get(selectedAccountId) || ''}
              videoFile={videoFile}
              logoFile={logoFile}
              onTextPositionChange={setTextPosition}
              onTextChange={(newText) => setTitles(prev => new Map(prev).set(selectedAccountId, newText))}
              onVideoUploadClick={handleVideoUploadClick}
              onLogoUploadClick={handleLogoUploadClick}
              backgroundColor={selectedTemplate?.backgroundColor}
              backgroundAlpha={selectedTemplate?.backgroundAlpha}
              textBoxStyle={selectedTemplate?.textBoxStyle}
              textBoxAngle={selectedTemplate?.textBoxAngle}
              textBoxRadius={selectedTemplate?.textBoxRadius}
              textBoxPadding={selectedTemplate?.textBoxPadding}
              textBoxPaddingTop={selectedTemplate?.textBoxPaddingTop}
              textBoxPaddingLeft={selectedTemplate?.textBoxPaddingLeft}
              textBoxPaddingRight={selectedTemplate?.textBoxPaddingRight}
              autoFontMax={selectedTemplate?.autoFontMax}
              colonBreak={selectedTemplate?.colonBreak}
              textZoneWidth={selectedTemplate?.textZoneWidth}
              textShadowEnabled={selectedTemplate?.textShadowEnabled}
              textShadowColor={selectedTemplate?.textShadowColor}
              textShadowBlur={selectedTemplate?.textShadowBlur}
              textShadowWidth={selectedTemplate?.textShadowWidth}
              textSoftShadowEnabled={selectedTemplate?.textSoftShadowEnabled}
              textSoftShadowColor={selectedTemplate?.textSoftShadowColor}
              textSoftShadowBlur={selectedTemplate?.textSoftShadowBlur}
              textSoftShadowOffsetX={selectedTemplate?.textSoftShadowOffsetX}
              textSoftShadowOffsetY={selectedTemplate?.textSoftShadowOffsetY}
              textSoftShadowOpacity={selectedTemplate?.textSoftShadowOpacity}
              textShadowOpacity={selectedTemplate?.textShadowOpacity}
              textColor={selectedTemplate?.textColor}
              useTwoColors={selectedTemplate?.useTwoColors}
              textColorSecondary={selectedTemplate?.textColorSecondary}
              fontFamily={selectedTemplate?.fontFamily}
              fontUrl={selectedTemplate?.fontUrl}
              textUppercase={selectedTemplate?.textUppercase}
              fontVariationSettings={selectedTemplate?.fontVariationSettings}
              logoPosition={selectedTemplate?.logoPosition}
              logoSize={selectedTemplate?.logoSize}
              frameSrc={selectedTemplate?.frameUrl}
              reelBackgroundUrl={selectedTemplate?.reelBackgroundUrl}
              textLineSpacing={selectedTemplate?.textLineSpacing}
              textAlign={selectedTemplate?.textAlign}
              textDuration={textDuration}
              fontSize={currentFontSize}
              onFontSizeChange={(size) => { if (selectedAccountId) setFontSizes(prev => new Map(prev).set(selectedAccountId, size)); }}
              resizeActive={sizeEditActive}
              onResizeActiveChange={setSizeEditActive}
              reelTextCenterX={selectedTemplate?.reelTextCenterX ?? 0}
              reelTextBottom={selectedTemplate?.reelTextBottom ?? 380}
            />
          )}
          <CopyrightBanner status={copyrightStatus} />
    </div>
  );

  return (
    <>
      {/* Hidden file inputs */}
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoChange}
        className="hidden"
      />
      <input
        ref={logoInputRef}
        type="file"
        accept="image/*"
        onChange={handleLogoChange}
        className="hidden"
      />
      <input
        ref={introImageInputRef}
        type="file"
        accept="image/*"
        onChange={handleIntroImageChange}
        className="hidden"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pb-24 lg:pb-0">
        {/* Левая колонка - Настройки (на мобильном - вторая) */}
        <div className="space-y-6 lg:order-1">
          {headerSlot}
          {montageFrom && (
            <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
              {montageLoading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Video className="h-4 w-4 shrink-0" />}
              <span className="min-w-0">
                {montageLoading ? 'Беру ролик из «Монтажа»…' : 'Ролик из «Монтажа»'}
                {montageTitle && <span className="text-muted-foreground"> — {montageTitle}</span>}
                {!montageLoading && !takeItem && montageSourceReady && montageItemAccount.resolved && checkedAccountIds.size === 0 && (
                  <span className="block text-xs text-muted-foreground">
                    {montageAccountIds.length === 0
                      ? 'У направления не указаны аккаунты — отметьте аккаунт вручную.'
                      : 'У ролика не выбран аккаунт — отметьте один: аккаунты направления стоят первыми.'}
                  </span>
                )}
              </span>
            </div>
          )}
          <StepHeading step={1} title="Куда публикуем" hint="Галочка — публикуем, тап — превью" />

          {/* Выбор аккаунта */}
          <div id="publish-accounts" className="space-y-2 scroll-mt-20">
            <Label>Аккаунты</Label>
            {/* Стор пуст и пока грузится ≠ «аккаунтов нет»: до фикса форма врала обоими случаями. */}
            {accountsLoading ? (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground bg-muted rounded-lg">
                Загружаем аккаунты…
              </div>
            ) : activeAccounts.length === 0 ? (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground bg-muted rounded-lg">
                {accountsError
                  ? 'Не удалось загрузить аккаунты — обновите страницу'
                  : 'Нет активных аккаунтов'}
              </div>
            ) : (
              <>
                {/* Поиск по аккаунтам */}
                <Input
                  type="text"
                  placeholder="Поиск аккаунта"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mb-3"
                />

                {/* Фильтр по тэгам */}
                {allTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {allTags.map(tag => (
                      <Badge
                        key={tag}
                        variant={selectedTags.has(tag) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => handleTagClick(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                
                <LastSetNotice
                  applied={appliedLast}
                  checked={checkedAccountIds}
                  lastIds={lastAccountIds}
                  availableIds={activeAccounts.map((a) => a.id)}
                  onReset={resetToEmpty}
                />
                <AccountGroupsBar
                  availableIds={activeAccounts.map((a) => a.id)}
                  checked={checkedAccountIds}
                  onChange={setCheckedAccountIds}
                  lastIds={lastAccountIds}
                />

                {filteredAccounts.length === 0 ? (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground bg-muted rounded-lg">
                    Ничего не нашлось
                  </div>
                ) : (
                  <AccountsHorizontalList
                    selectedAccountId={selectedAccountId}
                    onAccountSelect={(account: Account) => {
                      // Тап = только превью; галочку меняем через чекбокс (как в посте).
                      setSelectedAccountId(account.id);
                    }}
                    showCheckboxes={true}
                    onAccountCheck={handleAccountCheck}
                    onCheckMany={handleCheckMany}
                    checkedAccountIds={checkedAccountIds}
                    accounts={sortedAccounts}
                    allAccounts={activeAccounts}
                    accountsWithTemplates={new Set(templates.filter(t => (t.type ?? 'reel') === 'reel').map(t => t.accountId))}
                  />
                )}
              </>
            )}
            {templateMode === 'with' && selectedAccountId && !selectedTemplate && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-muted/60 px-3 py-2 text-sm">
                <span className="text-muted-foreground">У @{selectedAccount?.username || selectedAccount?.name} нет шаблона рилса.</span>
                <Link className="font-medium underline" to={`${ROUTES.TEMPLATE_NEW}?accountId=${encodeURIComponent(selectedAccountId)}`}>
                  Создать шаблон для @{selectedAccount?.username || selectedAccount?.name}
                </Link>
                <button type="button" className="font-medium underline" onClick={() => setTemplateMode('without')}>
                  Публиковать без шаблона
                </button>
              </div>
            )}
          </div>

          {/* Что публикуем: тип контента и сторис — рядом с «куда», а не в «когда». */}
          <div className="space-y-2">
            <Label htmlFor="content-type">
              Тип контента <span className="text-destructive">*</span>
            </Label>
            <Select
              value={publicationCategory || ''}
              onValueChange={(value) => setPublicationCategory(value as PublicationCategory)}
            >
              <SelectTrigger id="content-type" className="scroll-mt-20">
                <SelectValue placeholder="Выберите тип контента..." />
              </SelectTrigger>
              <SelectContent>
                {SELECTABLE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Дублировать в сторис: то же видео после рилса, на каждом аккаунте */}
          <div className="flex items-center justify-between">
            <Label htmlFor="reel-duplicate-story">Дублировать в сторис</Label>
            <Switch
              id="reel-duplicate-story"
              checked={duplicateToStory}
              onCheckedChange={setDuplicateToStory}
            />
          </div>

          {/* Узкий экран: превью здесь, под выбором аккаунтов, и его можно свернуть. */}
          {!isDesktop && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground truncate">
                  {videoFile ? `Видео: ${videoFile.name}` : 'Видео не загружено'}
                </span>
                <div className="flex shrink-0 gap-2">
                  {!previewOpen && !videoFile && (
                    <Button type="button" size="sm" variant="outline" onClick={handleVideoUploadClick}>
                      <Upload className="size-4" />
                      Загрузить видео
                    </Button>
                  )}
                  <Button type="button" size="sm" variant="ghost" onClick={() => setPreviewOpen((v) => !v)}>
                    {previewOpen ? 'Скрыть превью' : 'Показать превью'}
                  </Button>
                </div>
              </div>
              {previewOpen && <div className="mx-auto w-full max-w-[380px]">{previewContent}</div>}
            </div>
          )}

          <StepHeading step={2} title="Оформление и видео" hint="Шаблон аккаунта, заставка и текст на кадре" />

          {/* Режим шаблона */}
          <div className="space-y-2">
            <Tabs value={templateMode} onValueChange={(value) => setTemplateMode(value as TemplateMode)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="with">С шаблоном</TabsTrigger>
                <TabsTrigger value="without">Без шаблона</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Изображение для заставки - только в режиме "С шаблоном" */}
          {templateMode === 'with' && (
            <div className="space-y-2">
              <Label>Изображение для заставки</Label>
              <div className="flex items-start gap-3">
                {introImageFile ? (
                  <div className="relative aspect-[4/5] w-24 rounded-lg overflow-hidden border">
                    <img 
                      src={introImagePreviewUrl || ''} 
                      alt="Заставка"
                      className="w-full h-full object-cover"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      aria-label="Убрать заставку"
                      className="absolute right-1 top-1 size-6 rounded-full"
                      onClick={() => {
                        setIntroImageFile(null);
                        if (introImagePreviewUrl) {
                          URL.revokeObjectURL(introImagePreviewUrl);
                          setIntroImagePreviewUrl(null);
                        }
                      }}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </div>
                ) : null}
                {!introImageFile && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleIntroImageUploadClick}
                    className="flex-1"
                  >
                    <Upload />
                    Заставка
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Заголовок - только в режиме "С шаблоном" */}
          {templateMode === 'with' && (
            <div className="space-y-2">
              <TextScope
                checked={checkedAccountIds}
                value={textScope}
                selectedId={selectedAccountId}
                onChange={(groupId, previewId) => { setTextScope(groupId); if (previewId) setSelectedAccountId(previewId); }}
              />
              <div className="flex items-center justify-between">
                <Label>Текст заголовка</Label>
                <GenerateTitlesBulkButton
                  existingText={titles.get(selectedAccountId) || ''}
                  accounts={accountsForGeneration}
                  onTitlesGenerated={handleTitlesGenerated}
                />
              </div>
              <Textarea
                value={titles.get(selectedAccountId) || ''}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Текст на кадре"
                rows={2}
                maxLength={200}
              />
              <p className="text-right text-xs text-muted-foreground tabular-nums">
                {(titles.get(selectedAccountId) || '').length}/200
              </p>
            </div>
          )}

          {/* Размер шрифта рилса — СВОЙ на каждый аккаунт, как у постов */}
          {templateMode === 'with' && selectedTemplate && (
            <div className="space-y-2">
              <Label>Размер шрифта · {selectedAccount?.username || selectedAccount?.name}</Label>
              <FontSizeStepper
                value={resolvedFontSize}
                isAuto={currentFontSize == null}
                disabled={!selectedAccountId}
                onChange={handleFontSizeChange}
                onReset={() => {
                  if (!selectedAccountId) return;
                  setFontSizes((prev) => {
                    const next = new Map(prev);
                    next.delete(selectedAccountId);
                    return next;
                  });
                }}
              />
            </div>
          )}

          {/* Длительность отображения текста - только в режиме "С шаблоном" */}
          {templateMode === 'with' && (
            <div className="space-y-2">
              <Label>Текст на экране</Label>
              <Select 
                value={textDuration.toString()} 
                onValueChange={(value) => setTextDuration(value === 'full' ? 'full' : parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Сколько секунд" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 секунды</SelectItem>
                  <SelectItem value="5">5 секунд</SelectItem>
                  <SelectItem value="10">10 секунд</SelectItem>
                  <SelectItem value="full">На все видео</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <StepHeading step={3} title="Текст публикации" />

          {/* Описание */}
          <div className="space-y-2">
            <TextScope
              checked={checkedAccountIds}
              value={textScope}
              selectedId={selectedAccountId}
              onChange={(groupId, previewId) => { setTextScope(groupId); if (previewId) setSelectedAccountId(previewId); }}
            />
            <div className="flex items-center justify-between">
              <Label>Описание</Label>
              <GenerateCaptionsBulkButton
                existingText={description}
                accounts={accountsForGeneration}
                postType="reel"
                onCaptionsGenerated={handleCaptionsGenerated}
              />
            </div>
            <Textarea
              value={description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              placeholder="Добавьте описание к рилсу..."
              rows={4}
              maxLength={2200}
            />
            <p className="text-right text-xs text-muted-foreground tabular-nums">
              {description.length}/2200
            </p>
          </div>

          {/* Коллабораторы */}
          <div className="space-y-2">
            <CollaboratorsInput value={collaborators} onChange={setCollaborators} />
          </div>

          {/* Музыка — доступна только после загрузки видео (нужна длина окна) */}
          <div className="space-y-2">
            {videoFile ? (
              <MusicSection value={music} onChange={setMusic} videoDurationSec={videoDurationSec} />
            ) : (
              <p className="text-sm text-muted-foreground">
                Музыка — сначала загрузите видео
              </p>
            )}
          </div>

          <StepHeading step={4} title="Когда публикуем" />

          {/* Отложенная публикация: рендер идёт сразу, публикация — в очередь */}
          <WhenToPublish value={scheduledAt} onChange={setScheduledAt} />

          <WorkBriefToggle value={withBrief} onChange={setWithBrief} scheduled={!!scheduledAt} />


          {/* Экспорт — временно скрыто */}
          {false && (
          <div className="space-y-2">
            <Label>Экспорт</Label>
            <ExportReelButton
              videoFile={videoFile}
              logoFile={templateMode === 'with' ? logoFile : null}
              introImageFile={templateMode === 'with' ? introImageFile : null}
              textPosition={templateMode === 'with' ? textPosition : null}
              caption={templateMode === 'with' ? titles.get(selectedAccountId) || '' : ''}
              backgroundColor={templateMode === 'with' ? selectedTemplate?.backgroundColor : undefined}
              backgroundAlpha={templateMode === 'with' ? selectedTemplate?.backgroundAlpha : undefined}
              textShadowEnabled={templateMode === 'with' ? selectedTemplate?.textShadowEnabled : undefined}
              textShadowColor={templateMode === 'with' ? selectedTemplate?.textShadowColor : undefined}
              textShadowBlur={templateMode === 'with' ? selectedTemplate?.textShadowBlur : undefined}
              textShadowWidth={templateMode === 'with' ? selectedTemplate?.textShadowWidth : undefined}
              textSoftShadowEnabled={templateMode === 'with' ? selectedTemplate?.textSoftShadowEnabled : undefined}
              textSoftShadowColor={templateMode === 'with' ? selectedTemplate?.textSoftShadowColor : undefined}
              textSoftShadowBlur={templateMode === 'with' ? selectedTemplate?.textSoftShadowBlur : undefined}
              textColor={templateMode === 'with' ? selectedTemplate?.textColor : undefined}
              useTwoColors={templateMode === 'with' ? selectedTemplate?.useTwoColors : undefined}
              textColorSecondary={templateMode === 'with' ? selectedTemplate?.textColorSecondary : undefined}
              fontFamily={templateMode === 'with' ? selectedTemplate?.fontFamily : undefined}
              fontUrl={selectedTemplate?.fontUrl}
              textUppercase={templateMode === 'with' ? selectedTemplate?.textUppercase : undefined}
              fontVariationSettings={templateMode === 'with' ? selectedTemplate?.fontVariationSettings : undefined}
              logoPosition={templateMode === 'with' ? selectedTemplate?.logoPosition : undefined}
              textAlign={templateMode === 'with' ? selectedTemplate?.textAlign : undefined}
              hCenterX={templateMode === 'with' ? selectedTemplate?.reelTextCenterX : undefined}
              textDuration={textDuration}
              fontSize={templateMode === 'with' ? currentFontSize : null}
            />
          </div>
          )}
        </div>

        {/* Правая колонка - Превью */}
        <div className="hidden lg:block lg:order-2 mx-auto w-full max-w-[380px] lg:sticky lg:top-[72px] lg:self-start lg:max-h-[calc(100vh-88px)] lg:overflow-y-auto">
          {isDesktop && previewContent}
        </div>
      </div>

      {/* Монтируем VideoProcessor только когда обрабатываем */}
      {isProcessing && processingQueue.length > 0 && videoFile && textPosition && processingQueue.map(task => {
        const template = templates.find(t => t.accountId === task.accountId && (t.type ?? 'reel') === 'reel') || selectedTemplate;
        return (
        <VideoProcessor
          key={task.accountId}
          videoFile={videoFile}
          logoFile={task.logoFile}
          introImageFile={introImageFile}
          overlayText={titles.get(task.accountId) || ''}
          // Per-account client render: strip the selected account's pre-wrapped
          // lines so VideoProcessor wraps THIS account's own title (the shared
          // textPosition.lines belong to the previewed account only). The server
          // render path computes per-account lines in buildRenderTasks.
          textPosition={{ ...textPosition, lines: undefined }}
          onProgress={(progress) => {
            const p = Math.round(progress);
            setPublishStatuses(prev =>
              prev.map(s => s.account.id === task.accountId
                ? { ...s, progress: p }
                : s
              )
            );
            setOverallStatus(prev => prev ? {
              ...prev,
              progress: p,
              percent: calcOverallPercent('processing', prev.current, prev.total, true, p),
            } : prev);
          }}
          onComplete={(blob) => task.resolve(blob)}
          onError={(error) => task.reject(new Error(error))}
          backgroundColor={template?.backgroundColor}
          reelBackgroundUrl={template?.reelBackgroundUrl}
          backgroundAlpha={template?.backgroundAlpha}
          textBoxStyle={template?.textBoxStyle}
          textBoxAngle={template?.textBoxAngle}
          textBoxRadius={template?.textBoxRadius}
          textBoxPadding={template?.textBoxPadding}
          textBoxPaddingTop={template?.textBoxPaddingTop}
          textBoxPaddingLeft={template?.textBoxPaddingLeft}
          textBoxPaddingRight={template?.textBoxPaddingRight}
          textShadowEnabled={template?.textShadowEnabled}
          textShadowColor={template?.textShadowColor}
          textShadowBlur={template?.textShadowBlur}
          textShadowWidth={template?.textShadowWidth}
          textSoftShadowEnabled={template?.textSoftShadowEnabled}
          textSoftShadowColor={template?.textSoftShadowColor}
          textSoftShadowBlur={template?.textSoftShadowBlur}
          textColor={template?.textColor}
          useTwoColors={template?.useTwoColors}
          textColorSecondary={template?.textColorSecondary}
          fontFamily={template?.fontFamily}
          fontUrl={template?.fontUrl}
          textUppercase={template?.textUppercase}
          fontVariationSettings={template?.fontVariationSettings}
          logoPosition={template?.logoPosition}
          logoSize={template?.logoSize}
          lineSpacing={template?.textLineSpacing}
          textAlign={template?.textAlign}
          hCenterX={template?.reelTextCenterX}
          textDuration={textDuration}
          fontSize={fontSizes.get(task.accountId) ?? null}
        />
      )})}
      {/* VideoProcessor для превью (когда не публикуем) */}
      {isProcessing && videoFile && textPosition && processingQueue.length === 0 && (
        <VideoProcessor
          videoFile={videoFile}
          logoFile={logoFile}
          introImageFile={introImageFile}
          overlayText={titles.get(selectedAccountId) || ''}
          textPosition={textPosition}
          onProgress={(progress) => {
            console.log(`Processing: ${progress.toFixed(1)}%`);
          }}
          onComplete={handleProcessingComplete}
          onError={handleProcessingError}
          backgroundColor={selectedTemplate?.backgroundColor}
          reelBackgroundUrl={selectedTemplate?.reelBackgroundUrl}
          backgroundAlpha={selectedTemplate?.backgroundAlpha}
          textBoxStyle={selectedTemplate?.textBoxStyle}
          textBoxAngle={selectedTemplate?.textBoxAngle}
          textBoxRadius={selectedTemplate?.textBoxRadius}
          textBoxPadding={selectedTemplate?.textBoxPadding}
          textBoxPaddingTop={selectedTemplate?.textBoxPaddingTop}
          textBoxPaddingLeft={selectedTemplate?.textBoxPaddingLeft}
          textBoxPaddingRight={selectedTemplate?.textBoxPaddingRight}
          textColor={selectedTemplate?.textColor}
          useTwoColors={selectedTemplate?.useTwoColors}
          textColorSecondary={selectedTemplate?.textColorSecondary}
          fontFamily={selectedTemplate?.fontFamily}
          fontUrl={selectedTemplate?.fontUrl}
          textUppercase={selectedTemplate?.textUppercase}
          fontVariationSettings={selectedTemplate?.fontVariationSettings}
          logoPosition={selectedTemplate?.logoPosition}
          logoSize={selectedTemplate?.logoSize}
          lineSpacing={selectedTemplate?.textLineSpacing}
          textAlign={selectedTemplate?.textAlign}
          hCenterX={selectedTemplate?.reelTextCenterX}
          textDuration={textDuration}
          fontSize={currentFontSize}
        />
      )}

      {/* Нормализация видео в режиме "без шаблона" — приводит файл к формату,
          который Instagram Reels API гарантированно принимает (CFR 30fps,
          baseline H.264 через MediaRecorder). */}
      {isNormalizing && videoFile && (
        <ReelNormalizer
          videoFile={videoFile}
          onProgress={(progress) => {
            console.log(`Normalizing: ${progress.toFixed(1)}%`);
          }}
          onComplete={(blob) => {
            const resolve = (window as any).__reelNormalizerResolve;
            (window as any).__reelNormalizerResolve = null;
            (window as any).__reelNormalizerReject = null;
            setIsNormalizing(false);
            if (resolve) resolve(blob);
          }}
          onError={(error) => {
            const reject = (window as any).__reelNormalizerReject;
            (window as any).__reelNormalizerResolve = null;
            (window as any).__reelNormalizerReject = null;
            setIsNormalizing(false);
            if (reject) reject(new Error(error));
          }}
        />
      )}

      {/* Модалка подтверждения публикации */}
      <PublishConfirmationModal
        open={showPublishModal}
        onOpenChange={setShowPublishModal}
        accounts={publishStatuses}
        onConfirm={handleConfirmPublish}
        isPublishing={publishing}
        category={publicationCategory}
        copyrightStatus={copyrightStatus}
        overallStatus={overallStatus}
        videoDurationSec={videoDurationSec}
        onUserCancel={async () => {
          if (!currentJobId) return;
          publishAbortRef.current?.abort();
          publishAbortRef.current = null;
          await cancelReelJob(currentJobId);
          clearActiveJob();
          setCurrentJobId(null);
          setOverallStatus(null);
          setPublishing(false);
          setShowPublishModal(false);
          toast.info('Публикация отменена');
        }}
        done={publishDone}
        onAck={handleAckDone}
        errorState={!!publishError}
        errorReason={publishError ?? undefined}
        onRetry={handleRetry}
        uploadWarning={uploadWarning}
        retryNotice={scheduledNotice ?? retryNotice}
        scheduledAt={scheduledAt}
        duplicateToStory={duplicateToStory}
        batchId={lastBatchId}
        returnTo={returnTo}
        onPublishAgain={handleAckDone}
      />

      {/* Фиксированная кнопка внизу для мобильных */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-background border-t shadow-lg z-50"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
        {hint && <p className="mb-2 text-center text-xs text-muted-foreground">{hint}</p>}
        <Button
          onClick={handleOpenPublishModal}
          disabled={loading || publishing}
          size="lg"
          className="w-full"
        >
          {publishing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Публикуем...
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              {publishLabel}
            </>
          )}
        </Button>
      </div>
    </>
  );
}

interface AccountTiming {
  accountName: string;
  inputSizeMB: number;
  processedSizeMB: number;
  renderMs: number | null;
  supabaseUploadMs: number;
  remuxMs: number | null;
  igCreateMs: number;
  igPublishMs: number;
  totalMs: number;
}

function printAccountTiming(t: AccountTiming) {
  console.group(`📊 [${t.accountName}] — publish timing`);
  console.log(`📁 Input:      ${t.inputSizeMB.toFixed(2)} MB`);
  if (t.renderMs !== null) {
    console.log(`🎬 Render:     ${t.renderMs.toLocaleString()} ms`);
  } else {
    console.log(`🎬 Render:     — (no template)`);
  }
  console.log(`📦 Processed:  ${t.processedSizeMB.toFixed(2)} MB`);
  console.log(`☁️  Supabase:   ${t.supabaseUploadMs.toLocaleString()} ms`);
  console.log(`🔄 Remux:      ${t.remuxMs !== null ? `${t.remuxMs.toLocaleString()} ms` : '— skipped'}`);
  console.log(`📤 IG create:  ${t.igCreateMs.toLocaleString()} ms`);
  console.log(`✅ IG publish: ${t.igPublishMs.toLocaleString()} ms`);
  console.log(`⏱  Total:      ${t.totalMs.toLocaleString()} ms`);
  console.groupEnd();
}
